import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button, Modal, Form } from 'react-bootstrap';
import { AiOutlineFileAdd } from "react-icons/ai";
import { BsPencilFill, BsFileEarmarkCode } from "react-icons/bs";
import { LuFilter, LuX } from 'react-icons/lu';
import { GrAttachment } from "react-icons/gr";
import { IoSettingsOutline } from "react-icons/io5";
import { FaRegHandPaper, FaSortUp, FaSortDown, FaBox } from "react-icons/fa";
import { BiDownload } from "react-icons/bi";
import axios from 'axios';
import Header from '../../../header/header.js';
import Navvbar from '../../../header/Navvbar.js';
import { useCurrency } from '../../../../context/currencyContext';
import NotesSection from '../../../NotesSection.js';
import FileUploadModal from '../../../FileUploadModal.js';
import Swal from 'sweetalert2';

import usePricingAccess from '../../../../hooks/usePricingAccess.js';

import './casesOnHold.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://hms.backend.hiragroupcloud.com';

// =====================================================
// GET USER LAB ID
// =====================================================
const getUserLabId = () => {
  try {
    const possibleKeys = ['user', 'userData', 'auth', 'currentUser', 'labUser', 'labUserData'];

    for (const key of possibleKeys) {
      const storedData = localStorage.getItem(key);
      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);
          const userLabId =
            parsed.userLabId ||
            parsed.labId ||
            parsed.id ||
            (parsed.data && parsed.data.userLabId) ||
            (parsed.data && parsed.data.labId) ||
            (parsed.user && parsed.user.userLabId) ||
            (parsed.user && parsed.user.labId);

          if (userLabId) return userLabId;

          if (typeof storedData === 'string' && storedData.match(/^000000\d{2}$/)) {
            return storedData;
          }
        } catch (e) {
          continue;
        }
      }
    }

    for (let i = 0; i < localStorage.length; i++) {
      const value = localStorage.getItem(localStorage.key(i));
      if (value && value.match(/^000000\d{2}$/)) {
        return value;
      }
    }

    return null;
  } catch (error) {
    return null;
  }
};

// Short display date like "1 Oct 2026"
const formatShortDate = (value) => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// =====================================================
// COLUMN SORT HELPERS
// =====================================================
const getSortValue = (c, key) => {
  switch (key) {
    case 'caseNumber':
      return Number(c.caseNumber) || String(c.caseNumber || '');
    case 'invoice':
      return Number(c.invoiceNumber) || '';
    case 'account':
      return c.account_name || c.account_holder || '';
    case 'provider':
      return c.provider_name || '';
    case 'patient':
      return c.last_name || '';
    case 'accountShip':
      return c.start_date ? new Date(c.start_date).getTime() : '';
    case 'labShip':
      return c.arrive_date ? new Date(c.arrive_date).getTime() : '';
    case 'delivery':
      return c.delivery_date ? new Date(c.delivery_date).getTime() : '';
    case 'status':
      return c.status || '';
    default:
      return '';
  }
};

const sortByColumn = (list, sortConfig) => {
  if (!sortConfig.key) return list;

  const dir = sortConfig.dir === 'desc' ? -1 : 1;

  return [...list].sort((a, b) => {
    const va = getSortValue(a, sortConfig.key);
    const vb = getSortValue(b, sortConfig.key);

    if (va === '' && vb === '') return 0;
    if (va === '') return 1;
    if (vb === '') return -1;

    if (typeof va === 'number' && typeof vb === 'number') {
      return (va - vb) * dir;
    }

    return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir;
  });
};

const SortHeader = ({ label, colKey, sortConfig, onSort }) => {
  const isActive = sortConfig.key === colKey;

  return (
    <th className="table-header sortable-th" onClick={() => onSort(colKey)}>
      {label}
      <span className="sort-arrows">
        <FaSortUp className={`sort-icon${isActive && sortConfig.dir === 'asc' ? ' active' : ''}`} />
        <FaSortDown className={`sort-icon${isActive && sortConfig.dir === 'desc' ? ' active' : ''}`} />
      </span>
    </th>
  );
};

// =====================================================
// MAIN COMPONENT
// =====================================================
const CasesOnHold = () => {
  const { formatAmount } = useCurrency();

  const { hasPricingAccess, loading: pricingLoading } = usePricingAccess();

  const navigate = useNavigate();
  const location = useLocation();
  const { accountId, accountName } = location.state || {};

  // =====================================================
  // STATE
  // =====================================================
  const [cases, setCases] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [dropdownVisible, setDropdownVisible] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [monthlyTotal, setMonthlyTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState(
    () => new URLSearchParams(location.search).get('filter') || 'thismonth'
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [uploadingStatus, setUploadingStatus] = useState({});
  const [uploadSuccess, setUploadSuccess] = useState({});

  const [sortConfig, setSortConfig] = useState({ key: '', dir: 'asc' });

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );
  };

  const dropdownRef = useRef({});
  const dropdownMenuRef = useRef({});
  const isInitialMount = useRef(true);
  const requestIdRef = useRef(0);

  // =====================================================
  // FILTERS
  // =====================================================
  const [filters, setFilters] = useState({
    filterType: 'account',
    filterValue: '',
    by: 'month',
    caseTag: 'any',
    properties: 'all',
    startDate: '',
    endDate: '',
    caseStatus: ''
  });

  const [filterOptions, setFilterOptions] = useState({
    accounts: [], providers: [], patients: [], caseIds: [], deliveryDates: [], caseTags: [], statuses: []
  });

  // =====================================================
  // SWEET ALERT HELPERS
  // =====================================================
  const showSuccessAlert = (title, message) => {
    Swal.fire({
      title, text: message, icon: 'success', toast: true,
      position: 'top-end', showConfirmButton: false, timer: 3000,
      timerProgressBar: true, background: '#ffffff', iconColor: '#004aad'
    });
  };

  const showErrorAlert = (title, message) => {
    Swal.fire({
      title, text: message, icon: 'error', toast: true,
      position: 'top-end', showConfirmButton: false, timer: 4000,
      timerProgressBar: true
    });
  };

  const showInfoAlert = (title, message) => {
    Swal.fire({
      title, text: message, icon: 'info', toast: true,
      position: 'top-end', showConfirmButton: false, timer: 2000,
      timerProgressBar: true
    });
  };

  // =====================================================
  // FORMAT HELPERS
  // =====================================================
  const formatInvoiceNumber = (number) => {
    return number ? String(number).padStart(6, '0') : 'N/A';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const formatAmountWithCommas = (amount) => {
    if (!hasPricingAccess) return null;
    if (typeof amount !== 'number' || isNaN(amount)) return null;
    const formatted = formatAmount(amount);
    if (typeof formatted !== 'string') return formatted;
    return formatted.replace(/\d+(\.\d+)?/g, (match) => {
      const parts = match.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    });
  };

  // =====================================================
  // GET DATE RANGES
  // =====================================================
  const getDateRanges = useCallback(() => {
    const today = new Date();
    const todayFormatted = formatDate(today);
    const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const thisWeekStart = new Date(today);
    thisWeekStart.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
    const thisWeekEnd = new Date(thisWeekStart);
    thisWeekEnd.setDate(thisWeekStart.getDate() + 6);

    return {
      today: todayFormatted,
      monthStart: formatDate(thisMonthStart),
      monthEnd: formatDate(thisMonthEnd),
      weekStart: formatDate(thisWeekStart),
      weekEnd: formatDate(thisWeekEnd),
    };
  }, []);

  // =====================================================
  // FETCH FILTER OPTIONS
  // =====================================================
  const fetchFilterOptions = useCallback(async () => {
    try {
      const userLabId = getUserLabId();
      if (!userLabId) return;

      const response = await axios.get(`${API_URL}/api/cases/filter-options`, {
        params: {
          userLabId,
          ...(accountId && { accountId }),
          statusFilter: 'onhold'
        }
      });

      if (response.data.success) {
        setFilterOptions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  }, [accountId]);

  // =====================================================
  // FETCH FILTERED CASES FROM BACKEND
  // =====================================================
  const fetchFilteredCases = useCallback(async (currentFilters, page = 1, currentFilterType = filterType) => {
    const requestId = ++requestIdRef.current;

    try {
      setFilterLoading(true);
      setError(null);

      const userLabId = getUserLabId();
      if (!userLabId) {
        setError("User Lab ID not found. Please login again.");
        return;
      }

      const now = new Date();

      const params = {
        userLabId,
        statusFilter: 'onhold',
        page,
        limit: itemsPerPage,
        filterType: currentFilters.filterType,
        filterValue: currentFilters.filterValue,
        by: currentFilters.by,
        caseTag: currentFilters.caseTag,
        properties: currentFilters.properties,
        startDate: currentFilters.startDate,
        endDate: currentFilters.endDate,
        caseStatus: currentFilters.caseStatus,
        currentMonth: now.getMonth(),
        currentYear: now.getFullYear(),
        ...(accountId && { accountId })
      };

      if (currentFilterType === 'today') {
        params.by = 'dateRange';
        params.startDate = getDateRanges().today;
        params.endDate = getDateRanges().today;
      } else if (currentFilterType === 'thisweek') {
        params.by = 'dateRange';
        params.startDate = getDateRanges().weekStart;
        params.endDate = getDateRanges().weekEnd;
      } else if (currentFilterType === 'forever') {
        params.by = 'dateRange';
        params.startDate = '';
        params.endDate = '';
      }

      const response = await axios.get(`${API_URL}/api/cases/lab-cases`, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
        params
      });

      if (requestId !== requestIdRef.current) return;

      if (response.data.success) {
        const casesData = response.data.data || [];
        setCases(casesData);

        const body = response.data;
        const meta = body.pagination || body.meta || {};

        const rawCount = body.totalCount ?? body.total ?? body.totalItems ?? body.totalCases ?? meta.totalCount ?? meta.total ?? meta.totalItems ?? meta.count;
        const parsedCount = Number(rawCount);
        const hasCount = rawCount !== undefined && rawCount !== null && !isNaN(parsedCount);

        const rawPages = body.totalPages ?? body.pages ?? body.pageCount ?? meta.totalPages ?? meta.pages ?? meta.pageCount;
        const parsedPages = Number(rawPages);
        const hasPages = rawPages !== undefined && rawPages !== null && !isNaN(parsedPages) && parsedPages > 0;

        let resolvedPages;
        if (hasPages) {
          resolvedPages = parsedPages;
        } else if (hasCount) {
          resolvedPages = Math.max(Math.ceil(parsedCount / itemsPerPage), 1);
        } else {
          resolvedPages = casesData.length >= itemsPerPage ? page + 1 : page;
        }

        setTotalPages(resolvedPages);
        setTotalCount(hasCount ? parsedCount : (page - 1) * itemsPerPage + casesData.length);

        const pageSum = casesData.reduce((sum, c) => sum + (c.totalPrice || 0), 0);
        setMonthlyTotal(
          typeof response.data.totalAmount === 'number' ? response.data.totalAmount : pageSum
        );
      } else {
        setError(response.data.message || 'Failed to load cases.');
      }
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error('Error fetching cases:', error);
      setError(error.response?.data?.message || 'Failed to load cases. Please try again later.');
    } finally {
      if (requestId === requestIdRef.current) {
        setFilterLoading(false);
        setLoading(false);
      }
    }
  }, [accountId, itemsPerPage, getDateRanges]);

  // =====================================================
  // FETCH ACCOUNTS
  // =====================================================
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const userLabId = getUserLabId();
        if (!userLabId) {
          setAccounts([]);
          return;
        }
        const response = await axios.get(`${API_URL}/api/account/lab-accounts/${userLabId}`);
        const formattedAccounts = (response.data.data || []).map(account => ({
          _id: account._id,
          name: account.account_name,
          accountId: account.accountId,
          account_type: account.account_type,
          city: account.city,
          zip: account.zip,
          address: account.address,
          telephone: account.telephone,
          address2: account.address2,
          email: account.email,
          country: account.country,
          website: account.website,
          state: account.state,
          time_zone: account.time_zone,
          status: account.status,
          ddxEnable: account.ddxEnable,
          createdAt: account.createdAt,
          updatedAt: account.updatedAt,
        }));
        setAccounts(formattedAccounts);
      } catch (error) {
        setAccounts([]);
      }
    };
    fetchAccounts();
  }, []);

  // =====================================================
  // HANDLE URL FILTER TYPE
  // =====================================================
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlFilterType = params.get('filter') || 'thismonth';
    setFilterType(urlFilterType);
  }, [location.search]);

  // =====================================================
  // UPDATE FILTERS WHEN FILTER TYPE CHANGES
  // =====================================================
  useEffect(() => {
    const dates = getDateRanges();
    let newFilters = { ...filters };

    switch (filterType) {
      case 'today':
        newFilters.by = 'dateRange';
        newFilters.startDate = dates.today;
        newFilters.endDate = dates.today;
        break;
      case 'thisweek':
        newFilters.by = 'dateRange';
        newFilters.startDate = dates.weekStart;
        newFilters.endDate = dates.weekEnd;
        break;
      case 'thismonth':
        newFilters.by = 'month';
        break;
      case 'forever':
        newFilters.by = 'dateRange';
        newFilters.startDate = '';
        newFilters.endDate = '';
        break;
      default:
        break;
    }

    setFilters(newFilters);
    setCurrentPage(1);

    if (!isInitialMount.current) {
      fetchFilteredCases(newFilters, 1, filterType);
    }
  }, [filterType]);

  // =====================================================
  // INITIAL FETCH
  // =====================================================
  useEffect(() => {
    fetchFilterOptions();
    fetchFilteredCases(filters, 1, filterType);
    isInitialMount.current = false;
  }, []);

  // =====================================================
  // REFETCH WHEN PAGE CHANGES
  // =====================================================
  useEffect(() => {
    if (isInitialMount.current) return;
    fetchFilteredCases(filters, currentPage, filterType);
  }, [currentPage]);

  // =====================================================
  // SAFETY: If current page goes out of range
  // =====================================================
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  // =====================================================
  // CSV EXPORT
  // =====================================================
  const exportToCSV = async () => {
    try {
      const userLabId = getUserLabId();
      if (!userLabId) {
        showErrorAlert('Error', 'User Lab ID not found.');
        return;
      }

      showInfoAlert('Exporting', 'Preparing your CSV file...');

      const now = new Date();
      const params = {
        userLabId,
        statusFilter: 'onhold',
        fetchAllFiltered: 'true',
        filterType: filters.filterType,
        filterValue: filters.filterValue,
        by: filters.by,
        caseTag: filters.caseTag,
        properties: filters.properties,
        startDate: filters.startDate,
        endDate: filters.endDate,
        caseStatus: filters.caseStatus,
        currentMonth: now.getMonth(),
        currentYear: now.getFullYear(),
        ...(accountId && { accountId })
      };

      if (filterType === 'today') {
        params.by = 'dateRange';
        params.startDate = getDateRanges().today;
        params.endDate = getDateRanges().today;
      } else if (filterType === 'thisweek') {
        params.by = 'dateRange';
        params.startDate = getDateRanges().weekStart;
        params.endDate = getDateRanges().weekEnd;
      } else if (filterType === 'forever') {
        params.by = 'dateRange';
        params.startDate = '';
        params.endDate = '';
      }

      const response = await axios.get(`${API_URL}/api/cases/lab-cases`, { params });
      const exportCases = response.data.data || [];

      if (exportCases.length === 0) {
        showInfoAlert('No Data', 'No cases to export.');
        return;
      }

      const headers = [
        'Case Number', 'Invoice Number', 'Account', 'Provider', 'Patient',
        'Pan Color', 'Total Price', 'Delivery Date', 'Status',
        'Account Ship Date', 'Lab Ship Date'
      ];

      const rows = exportCases.map(caseItem => [
        caseItem.caseNumber || '',
        caseItem.invoiceNumber ? formatInvoiceNumber(caseItem.invoiceNumber) : '',
        caseItem.account_name || '',
        caseItem.provider_name || '',
        caseItem.last_name || '',
        caseItem.color || '',
        hasPricingAccess ? (caseItem.totalPrice || '') : '',
        caseItem.delivery_date ? formatDate(caseItem.delivery_date) : '',
        caseItem.status || '',
        caseItem.start_date ? new Date(caseItem.start_date).toLocaleDateString() : '',
        caseItem.arrive_date ? new Date(caseItem.arrive_date).toLocaleDateString() : ''
      ]);

      const csvContent = [headers, ...rows]
        .map(row => row.map(cell => {
          if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))) {
            return `"${cell.replace(/"/g, '""')}"`;
          }
          return cell;
        }).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      const date = new Date();
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      link.setAttribute('href', url);
      link.setAttribute('download', `on-hold-cases-${dateStr}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showSuccessAlert('Export Successful', `Successfully exported ${exportCases.length} cases to CSV!`);
    } catch (error) {
      console.error('CSV Export Error:', error);
      showErrorAlert('Export Failed', 'Failed to export data to CSV. Please try again.');
    }
  };

  // =====================================================
  // FILE UPLOAD
  // =====================================================
  const fileUploadHelper = FileUploadModal({
    onUploadStart: (caseNumber) => {
      setUploadingStatus(prev => ({ ...prev, [caseNumber]: true }));
      setUploadSuccess(prev => ({ ...prev, [caseNumber]: false }));
      showInfoAlert('Uploading', `Uploading files for case #${caseNumber}...`);
    },
    onUploadSuccess: async (caseNumber, data) => {
      setUploadingStatus(prev => ({ ...prev, [caseNumber]: false }));
      setUploadSuccess(prev => ({ ...prev, [caseNumber]: true }));
      const uploadedFiles = data.files ? data.files.length : 0;
      showSuccessAlert('Upload Successful!', `${uploadedFiles} file(s) uploaded successfully for case #${caseNumber}`);
    },
    onUploadError: (caseNumber, errorMessage) => {
      setUploadingStatus(prev => ({ ...prev, [caseNumber]: false }));
      showErrorAlert('Upload Failed', `Failed to upload files for case #${caseNumber}: ${errorMessage}`);
    }
  });

  const handleUploadClick = (caseNumber, event) => {
    event.stopPropagation();
    fileUploadHelper.openFileExplorer(caseNumber);
    setDropdownVisible(null);
  };

  // =====================================================
  // NOTES
  // =====================================================
  const handleAddNoteClick = (caseNumber, event) => {
    event.stopPropagation();
    setSelectedCaseId(caseNumber);
    setShowNotesModal(true);
    setDropdownVisible(null);
  };

  const handleNotesUpdate = () => {
    showSuccessAlert('Note Added!', 'Note has been successfully added to the case.');
  };

  // =====================================================
  // FILTER HANDLERS
  // =====================================================
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setShowFilterModal(false);
    setCurrentPage(1);
    fetchFilteredCases(filters, 1, filterType);
    showSuccessAlert('Filters Applied', 'Your filters have been applied successfully!');
  };

  const handleResetFilters = () => {
    const resetFilters = {
      filterType: 'account', filterValue: '', by: 'month', caseTag: 'any',
      properties: 'all', startDate: '', endDate: '', caseStatus: ''
    };
    setFilters(resetFilters);
    setCurrentPage(1);
    fetchFilteredCases(resetFilters, 1, filterType);
    showInfoAlert('Filters Reset', 'All filters have been reset to default values.');
  };

  const getCurrentFilterOptions = () => {
    switch (filters.filterType) {
      case 'account': return filterOptions.accounts;
      case 'provider': return filterOptions.providers;
      case 'patient': return filterOptions.patients;
      case 'caseId': return filterOptions.caseIds;
      case 'delivery': return filterOptions.deliveryDates;
      default: return [];
    }
  };

  // =====================================================
  // DROPDOWN
  // =====================================================
  const toggleDropdown = (caseId, event) => {
    event.stopPropagation();
    setDropdownVisible(dropdownVisible === caseId ? null : caseId);
  };

  useEffect(() => {
    const handleClickOutside = event => {
      let isOutside = true;
      Object.keys(dropdownMenuRef.current).forEach(key => {
        if (dropdownMenuRef.current[key] && dropdownMenuRef.current[key].contains(event.target)) {
          isOutside = false;
        }
      });
      Object.keys(dropdownRef.current).forEach(key => {
        if (dropdownRef.current[key] && dropdownRef.current[key].contains(event.target)) {
          isOutside = false;
        }
      });
      if (isOutside) {
        setDropdownVisible(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // =====================================================
  // ACCOUNT CLICK
  // =====================================================
  const handleAccountClick = (accountName) => {
    const account = accounts.find(acc => acc.name === accountName);
    if (account) {
      navigate(`/tabsWorking/${account.accountId}`, { state: { accountData: account } });
    }
  };

  // =====================================================
  // PAGINATION
  // =====================================================
  const handleFirstPage = () => setCurrentPage(1);
  const handlePreviousPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const handleNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const handleLastPage = () => setCurrentPage(totalPages);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
      let endPage = startPage + maxPagesToShow - 1;
      if (endPage > totalPages) {
        endPage = totalPages;
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
      for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // =====================================================
  // TITLE
  // =====================================================
  const now = new Date();
  const monthName = now.toLocaleString('default', { month: 'long' });
  const currentYearValue = now.getFullYear();

  const getTitle = () => {
    const amountSuffix = hasPricingAccess
      ? ` (${formatAmountWithCommas(monthlyTotal)})`
      : '';

    if (accountName) return `All On Hold Cases for ${accountName}`;
    if (filterType === 'thismonth') return `On Hold Cases ${monthName} ${currentYearValue}${amountSuffix}`;
    if (filterType === 'today') return `On Hold Cases for Today${amountSuffix}`;
    if (filterType === 'thisweek') return `On Hold Cases for This Week${amountSuffix}`;
    if (filterType === 'forever') return `All On Hold Cases${amountSuffix}`;
    return `On Hold Cases${amountSuffix}`;
  };

  // =====================================================
  // LOADING / ERROR
  // =====================================================
  if (loading || pricingLoading) {
    return (
      <>
        <Header /><Navvbar />
        <div className="cases-loading">
          <p>Loading on-hold cases...</p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header /><Navvbar />
        <div className="cases-error">
          <p style={{ color: 'red' }}>{error}</p>
          <Button onClick={() => fetchFilteredCases(filters, 1, filterType)}>Retry</Button>
        </div>
      </>
    );
  }

  const displayCases = sortByColumn(cases, sortConfig);

  // =====================================================
  // RENDER
  // =====================================================
  return (
    <>
      <Header />
      <Navvbar />

      {selectedCaseId && (
        <NotesSection
          caseId={selectedCaseId}
          isModalOnly={true}
          showModal={showNotesModal}
          onCloseModal={() => {
            setShowNotesModal(false);
            setSelectedCaseId(null);
          }}
          onNotesUpdate={handleNotesUpdate}
        />
      )}

      <div className="cases-page cases-hold-page">

        {/* PAGE HEADER */}
        <div className="cases-header">
          <h2 className="cases-title">{getTitle()}</h2>
          <div className="action-buttons">
            <button
              className="tab-btn csv-btn"
              onClick={exportToCSV}
              disabled={cases.length === 0}
            >
              <BiDownload /> CSV
            </button>
            <button
              className="tab-btn filter-btn"
              onClick={() => setShowFilterModal(true)}
            >
              <LuFilter /> Filters
            </button>
          </div>
        </div>

        {/* LOADING OVERLAY */}
        {filterLoading && (
          <div className="filter-loading-overlay">
            <div className="loading-spinner"></div>
            <p>Loading cases...</p>
          </div>
        )}

        {/* TABLE */}
        <div className="cases-table-wrapper">
          <table className="cases-table">
            <colgroup>
              <col style={{ width: '6%' }} />
              <col style={{ width: '4%' }} />
              <col style={{ width: '17%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '7%' }} />
            </colgroup>

            <thead>
              <tr>
                <SortHeader
                  label={
                    <span className="icon-tooltip" data-tooltip="Case Status">
                      <FaBox className="table-icon header-box-icon icon-inbox" />
                    </span>
                  }
                  colKey="status"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <th className="table-header"><GrAttachment className="table-icon icon-attachment" /></th>
                <SortHeader label="Case/Pan" colKey="caseNumber" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Invoice" colKey="invoice" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Account" colKey="account" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Provider" colKey="provider" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Patient/Appt" colKey="patient" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Account Ship" colKey="accountShip" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Lab Ship" colKey="labShip" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Delivery Date" colKey="delivery" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="On Hold Status" colKey="status" sortConfig={sortConfig} onSort={handleSort} />
              </tr>
            </thead>
            <tbody>
              {displayCases.length > 0 ? (
                displayCases.map(caseItem => {
                  const invoiceData = caseItem.invoiceNumber ? {
                    invoiceNumber: caseItem.invoiceNumber,
                    invoiceTotal: caseItem.invoiceTotal,
                    caseId: caseItem.caseNumber,
                  } : null;

                  return (
                    <tr
                      key={caseItem._id}
                      className="case-row"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e0e0e0';
                        const cells = e.currentTarget.getElementsByTagName('td');
                        for (let i = 0; i < cells.length; i++) {
                          cells[i].style.backgroundColor = '#e0e0e0';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        const cells = e.currentTarget.getElementsByTagName('td');
                        for (let i = 0; i < cells.length; i++) {
                          cells[i].style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      {/* Hold status icon — same look as "Cases Opened" (solid black hand) */}
                      <td className="table-cell status-cell">
                        <span className="icon-tooltip" data-tooltip={caseItem.status || 'On Hold'}>
                          <FaRegHandPaper className="icon-inbox icon-hold" />
                        </span>
                      </td>
                      <td className="table-cell"><GrAttachment className="table-icon icon-attachment" /></td>
                      <td className="case-cell">
                        <div className="case-top-row">

                          <div className="case-content-wrapper">
                            <div className="case-number-wrapper">
                              <Link to={`/case/${caseItem.caseNumber}`} className="case-link">
                                #{caseItem.caseNumber}
                              </Link>
                              <button
                                ref={el => dropdownRef.current[caseItem._id] = el}
                                className="settings-button"
                                onClick={e => toggleDropdown(caseItem._id, e)}
                              >
                                <IoSettingsOutline className="settings-icon" />
                              </button>
                            </div>
                            <div className="pan-color">
                              <span>Pan Color:</span>
                              <span
                                className="pan-color-box"
                                style={{ backgroundColor: caseItem.color ? caseItem.color.toLowerCase() : "#ccc" }}
                              ></span>
                              <span>{caseItem.color || "N/A"}</span>
                            </div>
                            {caseItem.procedures && caseItem.procedures.length > 0 ? (
                              <div className="procedures">
                                {caseItem.procedures.slice(0, 2).map((proc, index) => (
                                  <div key={index}>{proc.procedure}</div>
                                ))}
                                {caseItem.procedures.length > 2 && (
                                  <div>+{caseItem.procedures.length - 2} more</div>
                                )}
                              </div>
                            ) : (
                              <div className="procedures">No procedures selected</div>
                            )}
                          </div>

                        </div>

                        {dropdownVisible === caseItem._id && (
                          <div
                            ref={el => dropdownMenuRef.current[caseItem._id] = el}
                            className="case-dropdown"
                          >
                            <div className="dropdown-item"><BsFileEarmarkCode /> Close/Invoice</div>
                            <div className="dropdown-item" onClick={e => handleAddNoteClick(caseItem.caseNumber, e)}>
                              <BsPencilFill /> Add Notes
                            </div>
                            <div className="dropdown-item dropdown-item-last" onClick={e => handleUploadClick(caseItem.caseNumber, e)}>
                              <AiOutlineFileAdd /> Upload Files
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="invoice-cell">
                        {caseItem.invoiceNumber ? (
                          <Link
                            to={`/invoices/${formatInvoiceNumber(caseItem.invoiceNumber)}`}
                            state={{ invoiceData, caseId: caseItem.caseNumber }}
                            className="invoice-link"
                          >
                            #{formatInvoiceNumber(caseItem.invoiceNumber)}
                          </Link>
                        ) : ''}
                        {hasPricingAccess &&
                          caseItem.totalPrice &&
                          typeof caseItem.totalPrice === 'number' && (
                            <div className="invoice-total">
                              {formatAmountWithCommas(caseItem.totalPrice)}
                            </div>
                          )}
                      </td>
                      <td className="table-cell account-cell">
                        <span
                          onClick={() => handleAccountClick(caseItem.account_name || caseItem.account_holder)}
                          className="account-link"
                        >
                          {caseItem.account_name || caseItem.account_holder || "N/A"}
                        </span>
                      </td>
                      <td className="table-cell">{caseItem.provider_name || "N/A"}</td>
                      <td className="table-cell">{caseItem.last_name || "N/A"}</td>
                      <td className="table-cell">{formatShortDate(caseItem.start_date)}</td>
                      <td className="table-cell">{formatShortDate(caseItem.arrive_date)}</td>
                      <td className="table-cell">{formatShortDate(caseItem.delivery_date)}</td>
                      <td className="table-cell">{caseItem.status || "N/A"}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="11" className="no-cases">
                    No on-hold cases match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {(cases.length > 0 || currentPage > 1 || totalPages > 1) && (
          <div className="pagination-container">
            <Button className="pagination-button" onClick={handleFirstPage} disabled={currentPage === 1}>
              First
            </Button>
            <Button className="pagination-button" onClick={handlePreviousPage} disabled={currentPage === 1}>
              Previous
            </Button>
            {getPageNumbers().map(number => (
              <Button
                key={number}
                className={`pagination-button ${currentPage === number ? 'active' : 'outline'}`}
                onClick={() => setCurrentPage(number)}
              >
                {number}
              </Button>
            ))}
            <Button className="pagination-button outline" onClick={handleNextPage} disabled={currentPage === totalPages}>
              Next
            </Button>
            <Button className="pagination-button outline" onClick={handleLastPage} disabled={currentPage === totalPages}>
              Last
            </Button>
          </div>
        )}
      </div>

      {/* FILTER MODAL */}
      <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)} size="lg" centered>
        <Modal.Header style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
          <Modal.Title style={{ fontWeight: 'bold', color: '#004aad' }}>Change Filters</Modal.Title>
          <button onClick={() => setShowFilterModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>
            <LuX />
          </button>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '20px' }}>
              <div style={{ flex: '1', minWidth: '250px' }}>
                <h5 style={{ color: '#004aad', marginBottom: '15px' }}>Filters</h5>

                <Form.Group controlId="filterType" style={{ marginBottom: '15px' }}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Filter By</Form.Label>
                  <Form.Select
                    name="filterType"
                    value={filters.filterType}
                    onChange={handleFilterChange}
                    style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                  >
                    <option value="account">Account</option>
                    <option value="provider">Provider</option>
                    <option value="patient">Patient</option>
                    <option value="caseId">Case ID</option>
                    <option value="delivery">Delivery Date</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="filterValue" style={{ marginBottom: '15px' }}>
                  <Form.Label style={{ fontWeight: 'bold' }}>
                    {filters.filterType === 'account' ? 'Account' :
                     filters.filterType === 'provider' ? 'Provider' :
                     filters.filterType === 'patient' ? 'Patient' :
                     filters.filterType === 'caseId' ? 'Case ID' : 'Delivery Date'}
                  </Form.Label>
                  <Form.Select
                    name="filterValue"
                    value={filters.filterValue}
                    onChange={handleFilterChange}
                    style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                  >
                    <option value="">All</option>
                    {getCurrentFilterOptions().map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="filterBy" style={{ marginBottom: '15px' }}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Date Range</Form.Label>
                  <Form.Select
                    name="by"
                    value={filters.by}
                    onChange={handleFilterChange}
                    style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                  >
                    <option value="month">Current Month</option>
                    <option value="dateRange">Custom Date Range</option>
                  </Form.Select>
                </Form.Group>

                {filters.by === 'dateRange' && (
                  <>
                    <Form.Group controlId="filterStartDate" style={{ marginBottom: '15px' }}>
                      <Form.Label style={{ fontWeight: 'bold' }}>Start Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="startDate"
                        value={filters.startDate}
                        onChange={handleFilterChange}
                        style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                      />
                    </Form.Group>
                    <Form.Group controlId="filterEndDate" style={{ marginBottom: '0' }}>
                      <Form.Label style={{ fontWeight: 'bold' }}>End Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="endDate"
                        value={filters.endDate}
                        onChange={handleFilterChange}
                        style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                      />
                    </Form.Group>
                  </>
                )}
              </div>

              <div style={{ flex: '1', minWidth: '250px' }}>
                <h5 style={{ color: '#004aad', marginBottom: '15px' }}>Case Status Options</h5>

                <Form.Group controlId="filterCaseTag" style={{ marginBottom: '15px' }}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Case Tag</Form.Label>
                  <Form.Select
                    name="caseTag"
                    value={filters.caseTag}
                    onChange={handleFilterChange}
                    style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                  >
                    <option value="any">Any</option>
                    {filterOptions.caseTags.map(tag => (
                      <option key={tag} value={tag}>{tag}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="filterCaseStatus" style={{ marginBottom: '15px' }}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Case Status</Form.Label>
                  <Form.Select
                    name="caseStatus"
                    value={filters.caseStatus}
                    onChange={handleFilterChange}
                    style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                  >
                    <option value="">All Statuses</option>
                    {filterOptions.statuses.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="filterProperties" style={{ marginBottom: '15px' }}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Properties</Form.Label>
                  <Form.Select
                    name="properties"
                    value={filters.properties}
                    onChange={handleFilterChange}
                    style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                  >
                    <option value="all">All</option>
                    <option value="digital">Digital Manufacturing Files</option>
                    <option value="enclosures">Enclosures</option>
                    <option value="both">Digital + Enclosures</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #dee2e6', padding: '15px' }}>
          <Button variant="secondary" onClick={handleResetFilters} style={{ borderRadius: '5px', padding: '8px 15px' }}>
            Reset
          </Button>
          <Button
            variant="primary"
            onClick={handleApplyFilters}
            style={{ backgroundColor: '#004aad', borderColor: '#004aad', borderRadius: '5px', padding: '8px 15px' }}
          >
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CasesOnHold;